/** tm/components/TaskStepDetail.tsx — 단계별 상세 (phase 기반 인라인 진행 UI + 편집 모드) */
import JudgeSelect from './JudgeSelect';
import type { Judge } from './JudgeSelect';
import { useState, useCallback, useEffect, useRef } from 'react';
import type { Task, Company, TaskStep, AIPreset, AIStepConfig, AIBizplanConfig, CollectStepConfig, CollectItem, CollectItemGuide } from '../types';
import { ceoContact } from '../types';
import { C, T, R, Badge } from './ui';
import AIStepConfigPanel from './AIStepConfigPanel';
import AIBizplanConfigPanel from './AIBizplanConfigPanel';
import CollectStepConfigPanel from './CollectStepConfigPanel';
import CollectDetailModal from './CollectDetailModal';
import CollectNotifyModal from './CollectNotifyModal';
import CollectResultView from './CollectResultView';
import * as api from '../api';
import { CLOSE_PREFIX, isClosed, needsResult, roundBadge, stepStruck } from '../flow/flowLogic';
import { useFlowActions } from '../flow/FlowActions';
import type { FlowBody } from '../api';

const STEP_ICONS: Record<string, string> = {
  work: '📄', collect: '📎', ai_questionnaire: '🤖', ai_bizplan: '📄',
  review: '👀', submit: '📤', meeting: '🤝',
};
const STEP_LABELS: Record<string, string> = {
  work: '일반 작업', collect: '자료수집', ai_questionnaire: 'AI 질문지',
  ai_bizplan: 'AI 사업계획서', review: '검토', submit: '제출', meeting: '미팅',
};
const STEP_TYPE_OPTS = [
  { key: 'work', icon: '📄', label: '일반' },
  { key: 'collect', icon: '📎', label: '자료수집' },
  { key: 'ai_questionnaire', icon: '🤖', label: 'AI 질문지' },
  { key: 'ai_bizplan', icon: '📄', label: 'AI 사업계획서' },
  { key: 'review', icon: '👀', label: '검토' },
  { key: 'submit', icon: '📤', label: '제출' },
  { key: 'meeting', icon: '🤝', label: '미팅' },
];
const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  '대기': { bg: '#F3F4F6', color: '#6B7280', label: '대기' },
  '진행': { bg: C.accentLt, color: C.accent, label: '진행' },
  '완료': { bg: C.greenLt, color: C.green, label: '완료' },
  'FP요청대기': { bg: '#F5F3FF', color: '#7C3AED', label: 'FP대기' },
  // 2026-10-12 고객 페이지 · FP 포털 — 회신 대기(업체 · FP 누구든 먼저 답하면 끝) · 업무 보류 · 중단(업체가 안 하기로 함)
  '회신대기': { bg: '#E1F8F5', color: '#0A7D74', label: '회신대기' },
  '보류': { bg: '#FFFFFF', color: '#6B7280', label: '보류' },
  '중단': { bg: '#4E5968', color: '#FFFFFF', label: '중단' },
};
/** 중단 이유 — 한 번 누르면 끝(글 입력 강제 없음 · 4차 시안) */
const STOP_REASONS = ['비용 부담', '다른 곳에서 진행', '필요 없어짐', '연락 안 됨', '기타'];
const kstToday = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);

interface Props {
  task: Task; company: Company; aiPresets: AIPreset[];
  onRefresh: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  hideHeader?: boolean;
}

// extras 타입 헬퍼
interface StepExtrasData {
  collect_project_id?: string;
  collect_sent?: boolean;
  collect_ids?: string[];
  custom_docs?: string[];
  submitted_count?: number;
  phase?: string;
  result?: string;
  docx_s3_key?: string;
  ai_preset?: string;
  ai_prompt?: string;
  ai_status?: string;
  ai_result?: string;
  aiConfig?: AIStepConfig;
  bizplanConfig?: AIBizplanConfig;
  collectConfig?: CollectStepConfig;
  /** 공고 마감 — 밀리지 않는 단계 (2026-10-12 · services/stepFlow) */
  fixed_due?: boolean;
  /** 회신 대기 (2026-10-12) — 대상 · 시작일 · 물어본 것(고객 페이지 · FP 포털에 보임) · 답한 사람 */
  wait_for?: string[];
  wait_since?: string;
  wait_question?: string;
  replied_by?: string;
  replied_at?: string;
  includeCollectAnswers?: boolean;
}

function getExtras(s: TaskStep): StepExtrasData {
  return (s.extras || {}) as StepExtrasData;
}

function fmtDate(d?: string) {
  if (!d) return '';
  const s = d.slice(0, 10);
  if (!s.includes('-')) return d;
  const [y, m, dd] = s.split('-');
  const yn = Number(y), mn = Number(m), dn = Number(dd);
  if (isNaN(yn) || isNaN(mn) || isNaN(dn) || yn > 2100 || yn < 2000) return s;
  return yn + '년 ' + mn + '월 ' + dn + '일';
}

export default function TaskStepDetail({ task, company, aiPresets, onRefresh, onToast, hideHeader }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<{ stepIdx: number; note: string } | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  // AI 질문지 로컬 상태
  const [editedQuestions, setEditedQuestions] = useState<string>('');
  const [modifyRequest, setModifyRequest] = useState<string>('');

  // AI 사업계획서 로컬 상태
  const [bizExtraInstructions, setBizExtraInstructions] = useState<string>('');

  // AI 설정 로컬 상태 (인라인 설정 패널용)
  const [aiConfigs, setAiConfigs] = useState<Record<string, AIStepConfig>>({});
  const [bizConfigs, setBizConfigs] = useState<Record<string, AIBizplanConfig>>({});

  // ── 편집 모드 ──
  const [editMode, setEditMode] = useState(false);
  const [editingStepDue, setEditingStepDue] = useState<string|null>(null);
  const [editingStepName, setEditingStepName] = useState<string|null>(null);
  const [stopAsk, setStopAsk] = useState(false);   // 업무 「중단」 이유 고르기
  const [waitAsk, setWaitAsk] = useState<{ stepId: string; who: string[]; q: string } | null>(null);   // 단계 「회신대기」 설정
  const [resultFor, setResultFor] = useState<string | null>(null);   // 결과 단계 — 승인 · 미승인 고르기 칸을 연 단계
  const [addingStep, setAddingStep] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [newStepType, setNewStepType] = useState('work');
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskIcon, setEditTaskIcon] = useState('');
  const [editTaskDeadline, setEditTaskDeadline] = useState('');
  const [editTaskStatus, setEditTaskStatus] = useState('');
  const [editSteps, setEditSteps] = useState<{ id: string; name: string; type: string; status: string; due: string; note: string; extras: StepExtrasData }[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  // 이 업체 저장공간 자료 (자료수집 항목 등록 시 중복 요청 방지용)
  const [companyStorage, setCompanyStorage] = useState<{ id: string; file_name: string }[]>([]);
  useEffect(() => {
    if (!company?.id) return;
    fetch(`/api/tm/companies/${company.id}/storage`, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } })
      .then(r => r.json()).then(d => setCompanyStorage(d.data || [])).catch(() => { /* noop */ });
  }, [company?.id]);

  // 자료수집 항목 설정 패널 토글
  const [collectConfigStepId, setCollectConfigStepId] = useState<string | null>(null);
  // V21_DND:2-Aa — 단계 드래그앤드롭
  const [stepDragIdx, setStepDragIdx] = useState<number | null>(null);
  const [stepDragOver, setStepDragOver] = useState<number | null>(null);
  const onStepDrop = async (targetIdx: number) => {
    if (stepDragIdx === null || stepDragIdx === targetIdx) { setStepDragIdx(null); setStepDragOver(null); return; }
    const arr = steps.slice();
    const m = arr.splice(stepDragIdx, 1)[0];
    arr.splice(targetIdx, 0, m);
    setStepDragIdx(null); setStepDragOver(null);
    try { await api.reorderSteps(task.id, arr.map(s => s.id)); onRefresh(); }
    catch { onToast('순서 변경 실패', 'error'); }
  };
  // 업무 상태 직접 변경 — FP요청대기로 바꾸면 모든 단계 기한 삭제
  const changeTaskStatus = async (status: string) => {
    if (status === '중단') { setStopAsk(true); return; }
    try {
      await api.updateTask(task.id, (task.status === '중단' ? { status, stop_reason: null, stopped_at: null } : { status }) as Partial<Task>);
      if (status === 'FP요청대기') {
        const allS = steps;
        await Promise.all(allS.map(s => api.updateStep(task.id, s.id, { status: '대기', due: null } as Record<string, unknown>)));
        onToast(allS.length ? `FP요청대기 · 하위 ${allS.length}단계 대기 전환 + 기한 삭제` : 'FP요청대기로 변경됨', 'success');
      } else {
        onToast('업무 상태 변경됨', 'success');
      }
      onRefresh();
    } catch { onToast('상태 변경 실패', 'error'); }
  };
  const stopTask = async (reason: string) => {
    try {
      await api.updateTask(task.id, { status: '중단', stop_reason: reason, stopped_at: kstToday() } as Partial<Task>);
      setStopAsk(false); onRefresh();
      onToast(`업무 중단 · ${reason} — 고객 페이지 · FP 포털 「끝난 · 멈춘 일」로 옮겨요`, 'success');
    } catch { onToast('상태 변경 실패', 'error'); }
  };
  // 단계 회신대기 — 업체 · FP(기본 둘 다), 물어본 것(선택). 누구든 먼저 답하면 「회신 받음」
  const startWait = async (s: TaskStep, who: string[], q: string) => {
    if (who.length === 0) { onToast('업체나 FP 중 하나는 골라 주세요', 'error'); return; }
    try {
      const r = await api.taskFlow(task.id, { action: 'wait', stepId: s.id, wait_for: who, question: q.trim() });   // 진행 규칙(services/stepFlow)
      if (!r.success) throw new Error(r.error || '실패');
      setWaitAsk(null); onRefresh();
      onToast(`회신 대기 · ${who.join('·')} — 고객 페이지 · FP 포털에 보여요`, 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '실패', 'error'); }
  };
  const gotReply = async (s: TaskStep, by: string) => {
    try {
      const r = await api.taskFlow(task.id, { action: 'reply', stepId: s.id, by });   // 기다린 날만큼 기한 밀기
      if (!r.success) throw new Error(r.error || '실패');
      onRefresh(); onToast(r.data ? r.data.message : `회신 받음 · ${by}`, 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '실패', 'error'); }
  };
  // V20_MASTER:3a — 단계 기한 변경 시 후속 단계 연쇄 시프트
  const cascadeDueChange = async (stepId: string, oldDueRaw: string | null | undefined, newDue: string) => {
    try { await api.updateStep(task.id, stepId, { due: newDue || null } as Record<string, unknown>); }
    catch { onToast('기한 수정 실패', 'error'); return; }
    onToast('기한 수정됨', 'success');
    // V?_기한없음 cascade — 기한을 비우면(기한없음) 하위 단계도 모두 기한없음 처리할지 확인
    if (!newDue) {
      const idx0 = steps.findIndex(x => x.id === stepId);
      const tail0 = idx0 >= 0 ? steps.slice(idx0 + 1).filter(x => x.due) : [];
      if (tail0.length > 0 && window.confirm(`하위 단계 ${tail0.length}개도 모두 기한없음으로 할까요?`)) {
        await Promise.all(tail0.map(s => api.updateStep(task.id, s.id, { due: null } as Record<string, unknown>)));
        onToast(`하위 단계 ${tail0.length}개 기한없음 처리됨`, 'success');
      }
      onRefresh();
      return;
    }
    const oldDue = (oldDueRaw || '').slice(0, 10);
    if (oldDue && newDue && oldDue.length === 10 && newDue.length === 10) {
      const od = new Date(oldDue + 'T00:00:00');
      const nd = new Date(newDue + 'T00:00:00');
      const deltaDays = Math.round((nd.getTime() - od.getTime()) / 86400000);
      if (deltaDays !== 0) {
        const idx = steps.findIndex(x => x.id === stepId);
        const tail = idx >= 0 ? steps.slice(idx + 1).filter(x => x.due) : [];
        if (tail.length > 0) {
          const sign = deltaDays > 0 ? '+' : '';
          if (window.confirm(`뒤 단계 ${tail.length}개의 기한도 ${sign}${deltaDays}일 함께 조정할까요?`)) {
            for (const s of tail) {
              const sd = new Date(s.due!.slice(0, 10) + 'T00:00:00');
              sd.setDate(sd.getDate() + deltaDays);
              const ymd = `${sd.getFullYear()}-${String(sd.getMonth()+1).padStart(2,'0')}-${String(sd.getDate()).padStart(2,'0')}`;
              try { await api.updateStep(task.id, s.id, { due: ymd } as Record<string, unknown>); } catch { /* ignore */ }
            }
          }
        }
      }
    }
    onRefresh();
  };
  // 자료수집 현황 모달
  const [collectDetailProjectId, setCollectDetailProjectId] = useState<string | null>(null);
  // 자료수집 알림 발송 모달
  const [collectNotifyProjectId, setCollectNotifyProjectId] = useState<string | null>(null);
  // 자료수집 프로젝트 items 오버라이드 (자료수집 모듈에서 편집된 최신 데이터)
  const [projectItemsOverride, setProjectItemsOverride] = useState<Record<string, CollectStepConfig>>({});
  const loadedPidsRef = useRef<Set<string>>(new Set());
  // 탭 상태 (step별)
  const [stepTabs, setStepTabs] = useState<Record<string, number>>({});
  function getTab(stepId: string) { return stepTabs[stepId] || 0; }
  function setTab(stepId: string, tab: number) { setStepTabs(prev => ({ ...prev, [stepId]: tab })); }

  // 프로젝트가 생성된 collect step들에 대해 DB에서 최신 items를 가져와 덮어씀
  // (자료수집 모듈에서 편집된 내용을 업무관리 탭에서도 볼 수 있도록)
  useEffect(() => {
    const collectSteps = (task.steps || []).filter(s => {
      const ex = (s.extras || {}) as StepExtrasData;
      return s.type === 'collect' && ex.collect_project_id;
    });
    if (collectSteps.length === 0) return;
    (async () => {
      const next: Record<string, CollectStepConfig> = {};
      for (const s of collectSteps) {
        const ex = (s.extras || {}) as StepExtrasData;
        const pid = ex.collect_project_id!;
        // #3+#12 fix: 이미 로드한 pid는 건너뜀 (사용자 미저장 변경 보존)
        if (loadedPidsRef.current.has(pid)) continue;
        loadedPidsRef.current.add(pid);
        try {
          const res = await api.getCollectProject(pid);
          const p = res?.data || res;
          if (p && p.items) {
            next[s.id] = {
              items: (p.items as Array<Record<string, unknown>>).map(it => ({
                id: String(it.id || ''),
                label: String(it.label || ''),
                type: (String(it.type || 'file')) as CollectItem['type'],
                required: Boolean(it.required),
                sensitive: Boolean(it.sensitive),
                description: String(it.description || ''),
                section: it.section ? String(it.section) : undefined,
                options: Array.isArray(it.options) ? it.options as string[] : (typeof it.options === 'string' ? (() => { try { return JSON.parse(it.options as string); } catch { return []; } })() : []),
                guides: Array.isArray(it.guides) ? it.guides as CollectItemGuide[] : [],
              })),
              refFiles: (ex.collectConfig?.refFiles) || [],
              refLinks: (ex.collectConfig?.refLinks) || [],
              baseTemplateId: (ex.collectConfig?.baseTemplateId) || '',
            };
          }
        } catch { /* skip */ }
      }
      if (Object.keys(next).length > 0) setProjectItemsOverride(prev => ({ ...prev, ...next }));
    })();
  }, [task.steps]);

  const EDIT_STEP_TYPES = [
    { key: 'work', label: '일반 작업', icon: '⚙️' },
    { key: 'collect', label: '자료수집', icon: '📂' },
    { key: 'ai_questionnaire', label: 'AI 질문지', icon: '🤖' },
    { key: 'ai_bizplan', label: 'AI 사업계획서', icon: '📊' },
  ];

  function enterEditMode() {
    setEditTaskName(task.type || '');
    setEditTaskIcon(task.icon || '📋');
    setEditTaskDeadline(task.deadline ? task.deadline.slice(0, 10) : '');
    setEditTaskStatus(task.status || '진행');
    setEditSteps(steps.map(s => ({
      id: s.id, name: s.name, type: s.type || 'work',
      status: s.status || '대기', due: s.due ? s.due.slice(0, 10) : '',
      note: s.note || '', extras: getExtras(s),
    })));
    setEditMode(true);
  }

  function cancelEdit() { setEditMode(false); }

  function addEditStep() {
    setEditSteps(prev => [...prev, {
      id: 'new_' + Date.now(), name: '', type: 'work', status: '대기', due: '', note: '', extras: {},
    }]);
  }

  function removeEditStep(idx: number) {
    setEditSteps(prev => prev.filter((_, i) => i !== idx));
  }

  function updateEditStep(idx: number, key: string, value: string) {
    setEditSteps(prev => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  }

  async function saveEdit() {
    setEditSaving(true);
    try {
      // [#5] 상위 deadline 변경 시 하위 단계 due 도 같은 일수만큼 자동 시프트
      const oldDl = task.deadline ? task.deadline.slice(0, 10) : '';
      const newDl = editTaskDeadline || '';
      let shiftDays = 0;
      if (oldDl && newDl && oldDl !== newDl) {
        const ms = new Date(newDl + 'T00:00:00').getTime() - new Date(oldDl + 'T00:00:00').getTime();
        shiftDays = Math.round(ms / 86400000);
      }
      const shiftedSteps = editSteps.map(s => {
        if (!s.due || shiftDays === 0) return s;
        const d = new Date(s.due + 'T00:00:00');
        d.setDate(d.getDate() + shiftDays);
        const yr = d.getFullYear();
        if (yr < 1970 || yr > 9999) return s;
        const newDue = `${yr}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        return { ...s, due: newDue };
      });
      await api.bulkUpdateSteps(task.id, {
        taskData: {
          type: editTaskName,
          icon: editTaskIcon,
          status: editTaskStatus as 'FP요청대기' | '진행' | '완료',
          deadline: editTaskDeadline || null,
        } as Partial<Task>,
        steps: shiftedSteps.map(s => ({
          name: s.name, type: s.type as TaskStep['type'], status: s.status as TaskStep['status'],
          due: editTaskStatus === 'FP요청대기' ? null : (s.due || null), note: s.note, extras: s.extras as unknown as TaskStep['extras'],
        })),
      });
      const msg = shiftDays !== 0 ? `업무 수정 완료 (하위 단계 ${shiftDays > 0 ? '+' : ''}${shiftDays}일 자동 조정)` : '업무가 수정되었습니다';
      onToast(msg, 'success');
      setEditMode(false);
      onRefresh();
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '수정 실패', 'error');
    }
    setEditSaving(false);
  }

  // 자료수집 항목 설정 저장 + 프로젝트 DB 싱크
  async function saveCollectConfig(stepId: string, config: CollectStepConfig) {
    const step = steps.find(s => s.id === stepId);
    if (!step) return;
    const ext = getExtras(step);

    // #3+#12 fix: DB 싱크를 먼저 수행 (updateExtras → onRefresh → useEffect 재조회 race 방지)
    const pid = ext.collect_project_id;
    if (pid) {
      try {
        const res = await api.getCollectProject(pid);
        const dbItems = (res?.data?.items || res?.items || []) as Array<{ id: string; label: string }>;
        const localItems = config.items || [];

        // 2-1. 로컬에만 있는 항목 → DB에 추가 (label 기준 매칭)
        const dbLabels = new Set(dbItems.map(it => it.label));
        for (const li of localItems) {
          if (!li.label.trim()) continue;
          if (!dbLabels.has(li.label)) {
            await api.addProjectItem(pid, {
              label: li.label, type: li.type, required: li.required,
              sensitive: li.sensitive, description: li.description,
              options: li.options, guides: li.guides,
            });
          }
        }

        // 2-2. 로컬에 없는 항목 → DB에서 삭제 (label 기준)
        const localLabels = new Set(localItems.map(it => it.label));
        for (const di of dbItems) {
          if (!localLabels.has(di.label)) {
            await api.deleteProjectItem(pid, di.id);
          }
        }

        // 2-3. 양쪽 모두 있는 항목 → 속성 업데이트 (type/required/description 등)
        for (const li of localItems) {
          const di = dbItems.find(d => d.label === li.label);
          if (di) {
            await api.updateProjectItem(pid, di.id, {
              label: li.label, type: li.type, required: li.required,
              sensitive: li.sensitive, description: li.description,
              options: li.options,
            });
          }
        }
      } catch (e) {
        console.error('프로젝트 싱크 실패:', e);
        onToast('DB 싱크 중 오류', 'error');
        setCollectConfigStepId(null);
        return;
      }
    }

    // #3+#12 fix: DB 싱크 완료 후 task.extras 저장 + ref 초기화로 다음 조회 허용
    await updateExtras(stepId, { ...ext, collectConfig: config });
    if (pid) loadedPidsRef.current.delete(pid);
    onToast('자료수집 항목이 저장되었습니다', 'success');
    setCollectConfigStepId(null);
  }

  const steps = task.steps || [];
  const doneCount = steps.filter(s => s.status === '완료').length;
  const progress = steps.length > 0 ? Math.round(doneCount / steps.length * 100) : 0;

  const updateExtras = useCallback(async (stepId: string, extras: StepExtrasData) => {
    try {
      await api.updateStep(task.id, stepId, { extras } as Record<string, unknown>);
      onRefresh();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '실패', 'error'); }
  }, [task.id, onRefresh, onToast]);

  // 라이브 자료수집 항목 동기화: 이미 발송된 항목의 '변경된 스칼라 필드만' PUT(실제 id 매칭) →
  // 삽입·삭제 없이 기존 항목만 갱신하므로 안전, 포털(섹션/묶음 명칭 등)에 즉시 반영됨
  const syncLiveCollectItems = useCallback(async (pid: string, oldItems: CollectItem[], newItems: CollectItem[]) => {
    const oldById = new Map(oldItems.map(it => [it.id, it]));
    for (const it of newItems) {
      if (!it.id) continue;                 // 라이브 id 없는 신규 초안 항목은 건드리지 않음(삽입 안 함)
      const o = oldById.get(it.id);
      if (!o) continue;
      const patch: Record<string, unknown> = {};
      if ((it.section || '') !== (o.section || '')) patch.section = it.section || '';
      if (it.label !== o.label) patch.label = it.label;
      if ((it.description || '') !== (o.description || '')) patch.description = it.description || '';
      if (it.required !== o.required) patch.required = it.required;
      if (it.sensitive !== o.sensitive) patch.sensitive = it.sensitive;
      if (it.type !== o.type) patch.type = it.type;
      if (Object.keys(patch).length === 0) continue;
      try { await api.updateProjectItem(pid, it.id, patch); } catch { /* 동기화 실패는 조용히 무시(초안은 이미 저장됨) */ }
    }
  }, []);

  async function updateStepStatus(stepId: string, status: string) {
    try {
      const tgt0 = steps.find(x => x.id === stepId);
      // 결과 단계는 ✓ 완료 대신 승인 · 미승인(2026-10-18 빠진 입력 — 결과 없이 완료되면 고객 화면이 초록 체크 「끝났어요」)
      if (status === '완료' && tgt0 && needsResult(task, tgt0) && !getExtras(tgt0).result) { setResultFor(stepId); return; }
      // 회신 대기 → 진행 = 「회신 받음」(기다린 날만큼 기한 밀기 · 누가 답했는지 남김 · 2026-10-18)
      if (status === '진행' && tgt0 && tgt0.status === '회신대기') {
        const r = await api.taskFlow(task.id, { action: 'reply', stepId });
        if (!r.success) throw new Error(r.error || '실패');
        onRefresh(); onToast(r.data ? r.data.message : '회신 받음', 'success');
        return;
      }
      if (status === '완료') {   // 2026-10-12 — 완료는 진행 규칙으로(늦은 날만큼 뒤 단계 밀기 · 다음 단계 진행)
        const r = await api.taskFlow(task.id, { action: 'done', stepId });
        if (!r.success) throw new Error(r.error || '실패');
        onRefresh(); onToast(r.data ? r.data.message : '완료', 'success');
        return;
      }
      await api.updateStep(task.id, stepId, { status } as Record<string, unknown>);
      // 자료수집 단계를 진행으로 바꾸면 포털 자동 생성 (항목 있고 아직 미생성일 때)
      if (status === '진행') {
        const tgt = steps.find(x => x.id === stepId);
        if (tgt && tgt.type === 'collect') {
          const ex = getExtras(tgt);
          if (!ex.collect_project_id && (ex.collectConfig?.items?.length || 0) > 0) {
            await handleCollectStart(tgt, false);
            return;
          }
        }
      }
      onRefresh();
      onToast(`단계 상태: ${status}`, 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '실패', 'error'); }
  }

  async function saveNote(stepId: string, note: string) {
    try {
      await api.updateStep(task.id, stepId, { note });
      onRefresh(); setEditingNote(null);
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '실패', 'error'); }
  }

  // ── 자료수집 시작 (openNotify=false면 포털만 생성, 알림모달은 안 엶) ──
  async function handleCollectStart(s: TaskStep, openNotify = true): Promise<string | null> {
    const ext = getExtras(s);
    const config = ext.collectConfig;
    const items = config?.items || [];
    if (items.length === 0) {
      onToast('자료수집 항목이 설정되지 않았습니다', 'error');
      return null;
    }
    // 이미 생성된 프로젝트가 있으면 재사용
    if (ext.collect_project_id) {
      if (openNotify) setCollectNotifyProjectId(ext.collect_project_id);
      return ext.collect_project_id;
    }
    setLoading(s.id);
    try {
      const fpRow = company.fps?.[0];
      const res = await api.createCollectProjectFull({
        name: company.name,
        company_id: company.id,
        fp_id: fpRow?.fp_id,
        items: items.map(it => ({
          label: it.label, type: it.type, required: it.required,
          description: it.description, section: it.section || null, guides: it.guides, options: it.options,
        })),
        source: 'task_collect',
        task_id: task.id,
        step_id: s.id,
        brand: config?.brand || 'roum',
      });
      const pid = res.data.project_id;
      await updateExtras(s.id, { ...ext, collect_project_id: pid, collect_sent: true });
      // 단계 대기 → 진행 + 아코디언 접기
      if (s.status !== '진행' && s.status !== '완료') {
        try { await api.updateStep(task.id, s.id, { status: '진행' } as Record<string, unknown>); } catch { /* 실패해도 진행 */ }
      }
      setExpanded(null);
      onToast('자료수집 프로젝트가 생성되었습니다', 'success');
      if (openNotify) setCollectNotifyProjectId(pid);
      onRefresh();
      setLoading(null);
      return pid;
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '자료수집 생성 실패', 'error');
      setLoading(null);
      return null;
    }
  }

  // ── AI 질문지 생성 ──
  async function handleGenerateQuestionnaire(s: TaskStep) {
    const ext = getExtras(s);
    const cfg = aiConfigs[s.id] || ext.aiConfig || { presetId: '', files: [], extraPrompt: '', phase: 'settings' as const, result: '' };
    setLoading(s.id);
    try {
      // 앞선 collect 단계의 project_id 찾기
      const collectStep = steps.find(st => st.type === 'collect' && getExtras(st).collect_project_id);
      const collectProjectId = collectStep ? getExtras(collectStep).collect_project_id : undefined;

      const res = await api.generateQuestionnaire({
        files: cfg.files,
        extraPrompt: cfg.extraPrompt,
        includeCollectAnswers: cfg.includeCollectAnswers,
        collectProjectId,
        includeCollectFiles: !!(cfg as unknown as Record<string,unknown>).includeCollectFiles,
        taskId: task.id,
      });
      const questions = res.data.questions;
      setEditedQuestions(questions);
      await updateExtras(s.id, { ...ext, phase: 'reviewing', result: questions, aiConfig: cfg });
      onToast('질문지가 생성되었습니다', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : 'AI 질문지 생성 실패', 'error'); }
    setLoading(null);
  }

  // ── AI 질문지 수정 요청 재생성 ──
  async function handleRegenerateQuestionnaire(s: TaskStep, extraInstruction?: string) {
    const ext = getExtras(s);
    const cfg = aiConfigs[s.id] || ext.aiConfig || { presetId: '', files: [], extraPrompt: '', phase: 'reviewing' as const, result: '' };
    const prompt = (cfg.extraPrompt || '') + (extraInstruction ? '\n\n수정 요청: ' + extraInstruction : '');
    setLoading(s.id);
    try {
      const collectStep = steps.find(st => st.type === 'collect' && getExtras(st).collect_project_id);
      const collectProjectId = collectStep ? getExtras(collectStep).collect_project_id : undefined;

      const res = await api.generateQuestionnaire({
        files: cfg.files,
        extraPrompt: prompt,
        includeCollectAnswers: cfg.includeCollectAnswers,
        collectProjectId,
        includeCollectFiles: !!(cfg as unknown as Record<string,unknown>).includeCollectFiles,
        taskId: task.id,
      });
      const questions = res.data.questions;
      setEditedQuestions(questions);
      await updateExtras(s.id, { ...ext, phase: 'reviewing', result: questions });
      setModifyRequest('');
      onToast('질문지가 재생성되었습니다', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '재생성 실패', 'error'); }
    setLoading(null);
  }

  // ── 질문지 컨펌 → 자료수집 발송 ──
  async function handleConfirmQuestionnaire(s: TaskStep) {
    const ext = getExtras(s);
    const text = editedQuestions || ext.result || '';
    const lines = text.split('\n').filter(l => l.trim()).map(l => l.replace(/^\d+[\.\)]\s*/, '').trim());
    if (lines.length === 0) {
      onToast('질문지 내용이 없습니다', 'error');
      return;
    }
    setLoading(s.id);
    try {
      const fpRow = company.fps?.[0];
      const res = await api.createCollectProjectFull({
        name: company.name,
        company_id: company.id,
        fp_id: fpRow?.fp_id,
        items: lines.map(l => ({ label: l, type: 'text_long', required: true })),
        source: 'ai_questionnaire',
        task_id: task.id,
        step_id: s.id,
      });
      await updateExtras(s.id, { ...ext, phase: 'done', collect_project_id: res.data.project_id });
      onToast('질문지가 발송되었습니다', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '발송 실패', 'error'); }
    setLoading(null);
  }

  // ── AI 사업계획서 생성 ──
  async function handleGenerateBizplan(s: TaskStep) {
    const ext = getExtras(s);
    const cfg = bizConfigs[s.id] || ext.bizplanConfig || { presetId: '', files: [], extraPrompt: '', phase: 'settings' as const };
    setLoading(s.id);
    try {
      const res = await api.generateBizplan({
        files: cfg.files,
        extraPrompt: cfg.extraPrompt,
      });
      await updateExtras(s.id, { ...ext, phase: 'reviewing', docx_s3_key: res.data.docx_s3_key, result: res.data.text, bizplanConfig: cfg });
      onToast('사업계획서가 생성되었습니다', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : 'AI 사업계획서 생성 실패', 'error'); }
    setLoading(null);
  }

  // ── 사업계획서 재생성 ──
  async function handleRegenerateBizplan(s: TaskStep) {
    const ext = getExtras(s);
    const cfg = bizConfigs[s.id] || ext.bizplanConfig || { presetId: '', files: [], extraPrompt: '', phase: 'reviewing' as const };
    const prompt = (cfg.extraPrompt || '') + (bizExtraInstructions ? '\n\n추가 지시사항: ' + bizExtraInstructions : '');
    setLoading(s.id);
    try {
      const res = await api.generateBizplan({ files: cfg.files, extraPrompt: prompt });
      await updateExtras(s.id, { ...ext, phase: 'reviewing', docx_s3_key: res.data.docx_s3_key, result: res.data.text });
      setBizExtraInstructions('');
      onToast('사업계획서가 재생성되었습니다', 'success');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '재생성 실패', 'error'); }
    setLoading(null);
  }

  // ── docx 다운로드 ──
  async function handleDownloadDocx(s3Key: string) {
    try {
      const res = await api.getDownloadUrl(s3Key);
      window.open(res.data.url, '_blank');
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '다운로드 실패', 'error'); }
  }

  // ── 인라인 진행 UI 렌더 ──
  function renderStepProgress(s: TaskStep, si: number) {
    const ext = getExtras(s);
    const phase = ext.phase || 'settings';
    const isStepLoading = loading === s.id;

    // ═══ COLLECT ═══
    if (s.type === 'collect') {
      if (ext.collect_project_id) {
        return (
          <div style={{ padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green }} />
              <span style={{ fontSize: T.sm, fontWeight: 600, color: C.green }}>자료수집 진행 중</span>
            </div>
            <button onClick={() => {
              window.location.href = '/collect?project_id=' + ext.collect_project_id;
            }} style={{
              padding: '8px 20px', borderRadius: R.md, border: 'none',
              background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              📋 자료수집 현황 보기
            </button>
          </div>
        );
      }
      return (
        <div style={{ padding: '10px 0' }}>
          <button onClick={() => handleCollectStart(s)} disabled={isStepLoading} style={{
            padding: '5px 14px', borderRadius: R.sm, border: `1px solid ${isStepLoading ? C.muted : C.accent}`,
            background: isStepLoading ? C.bgAlt : C.accentLt, color: isStepLoading ? C.muted : C.accent,
            fontSize: T.xs, fontWeight: 600, cursor: isStepLoading ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            {isStepLoading ? '생성 중...' : '▶ 자료수집 시작'}
          </button>
          {ext.collectConfig?.items?.length ? (
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }}>
              📎 {ext.collectConfig.items.length}개 항목 설정됨
            </div>
          ) : (
            <div style={{ fontSize: T.xs, color: C.orange, marginTop: 6 }}>
              ⚠ 자료수집 항목을 먼저 설정해주세요
            </div>
          )}
        </div>
      );
    }

    // ═══ AI QUESTIONNAIRE ═══
    if (s.type === 'ai_questionnaire') {
      const aiCfg = aiConfigs[s.id] || ext.aiConfig || {
        presetId: ext.ai_preset || '', files: [], extraPrompt: ext.ai_prompt || '',
        phase: (phase as AIStepConfig['phase']) || 'settings', result: ext.result || '',
        includeCollectAnswers: ext.includeCollectAnswers,
      };

      // 자료수집 단계의 project_id
      const collectStep = steps.find(st => st.type === 'collect' && getExtras(st).collect_project_id);
      const collectProjectId = collectStep ? getExtras(collectStep).collect_project_id : undefined;

      if (phase === 'done') {
        return (
          <div style={{ padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: C.greenLt, borderRadius: R.md, marginBottom: 8 }}>
              <span style={{ fontSize: T.xl }}>✅</span>
              <div>
                <div style={{ fontSize: T.sm, fontWeight: 700, color: C.green }}>발송 완료</div>
                <div style={{ fontSize: T.xs, color: C.sub }}>질문지가 고객에게 발송되었습니다</div>
              </div>
            </div>
            {ext.collect_project_id && (
              <button onClick={() => {
                window.location.href = '/collect?project_id=' + ext.collect_project_id;
              }} style={{
                padding: '6px 14px', borderRadius: R.sm, border: `1px solid ${C.accent}`,
                background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer',
              }}>
                📋 답변 현황 보기
              </button>
            )}
          </div>
        );
      }

      if (phase === 'reviewing') {
        const questions = editedQuestions || ext.result || '';
        const parsedItems = questions.split('\n').filter((l: string) => l.trim()).map((l: string, i: number) => ({
          num: i + 1,
          text: l.replace(/^\d+[\.\)]\s*/, '').trim(),
          type: 'text_long' as const,
        }));
        return (
          <div style={{ padding: '10px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>생성된 질문지 ({parsedItems.length}문항)</div>
              <Badge bg={C.purpleLt} color={'#7C3AED'}>🤖 AI 생성</Badge>
            </div>
            {/* 구조화된 항목 목록 */}
            <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden', marginBottom: 8 }}>
              {parsedItems.map((item: {num:number;text:string;type:string}, idx: number) => (
                <div key={idx} style={{ padding: '8px 12px', borderBottom: idx < parsedItems.length - 1 ? `1px solid ${C.border}` : 'none', background: idx % 2 === 0 ? C.bgAlt : C.white, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: T.xs, fontWeight: 700, color: C.accent, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.num}</span>
                  <span style={{ flex: 1, fontSize: T.sm, color: C.text }}>{item.text}</span>
                  <span style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: R.sm, background: C.accentLt, color: C.accent, flexShrink: 0 }}>장문</span>
                </div>
              ))}
            </div>
            {/* 원본 편집 토글 */}
            <details style={{ marginBottom: 8 }}>
              <summary style={{ fontSize: T.xs, color: C.muted, cursor: 'pointer', padding: '4px 0' }}>원본 텍스트 편집</summary>
              <textarea
                value={questions}
                onChange={e => setEditedQuestions(e.target.value)}
                rows={10}
                style={{ width: '100%', padding: '10px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6, marginTop: 6 }}
              />
            </details>
            {/* 수정 요청 */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
              <input value={modifyRequest} onChange={e => setModifyRequest(e.target.value)}
                placeholder="수정 요청 사항 입력..." onKeyDown={e => { if (e.key === 'Enter' && modifyRequest.trim()) handleRegenerateQuestionnaire(s, modifyRequest); }}
                style={{ flex: 1, padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
              <button onClick={() => handleRegenerateQuestionnaire(s, modifyRequest)} disabled={isStepLoading || !modifyRequest.trim()}
                style={{ padding: '7px 14px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: isStepLoading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>수정</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleRegenerateQuestionnaire(s)} disabled={isStepLoading}
                style={{ padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: isStepLoading ? 'default' : 'pointer' }}>
                {isStepLoading ? '생성 중...' : '🔄 재생성'}
              </button>
              <button onClick={() => handleConfirmQuestionnaire(s)} disabled={isStepLoading}
                style={{ padding: '8px 20px', borderRadius: R.md, border: 'none', background: isStepLoading ? C.muted : C.green, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: isStepLoading ? 'default' : 'pointer' }}>
                ✅ 컨펌 → 고객/FP 발송
              </button>
            </div>
          </div>
        );
      }

      // phase === 'settings' (default)
      return (
        <div style={{ padding: '10px 0' }}>
          <AIStepConfigPanel
            value={aiCfg}
            onChange={v => setAiConfigs(prev => ({ ...prev, [s.id]: v }))}
            presets={aiPresets}
            aiType="ai_questionnaire"
            collectProjectId={collectProjectId}
            onViewCollect={collectProjectId ? () => {
              window.location.href = '/collect?project_id=' + collectProjectId;
            } : undefined}
          />
          <button
            onClick={() => handleGenerateQuestionnaire(s)}
            disabled={isStepLoading}
            style={{
              marginTop: 12, padding: '10px 20px', borderRadius: R.md, border: 'none',
              background: isStepLoading ? C.muted : C.accent, color: C.white,
              fontSize: T.sm, fontWeight: 700, cursor: isStepLoading ? 'default' : 'pointer',
              width: '100%', textAlign: 'center',
            }}
          >
            {isStepLoading ? '🤖 AI가 질문지를 생성하고 있습니다...' : '▶ 질문지 생성하기'}
          </button>
        </div>
      );
    }

    // ═══ AI BIZPLAN ═══
    if (s.type === 'ai_bizplan') {
      const bizCfg = bizConfigs[s.id] || ext.bizplanConfig || {
        presetId: ext.ai_preset || '', files: [], extraPrompt: ext.ai_prompt || '',
        phase: (phase as AIBizplanConfig['phase']) || 'settings',
      };

      if (phase === 'done') {
        return (
          <div style={{ padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: C.greenLt, borderRadius: R.md, marginBottom: 8 }}>
              <span style={{ fontSize: T.xl }}>✅</span>
              <span style={{ fontSize: T.sm, fontWeight: 700, color: C.green }}>완료됨</span>
            </div>
            {ext.docx_s3_key && (
              <button onClick={() => handleDownloadDocx(ext.docx_s3_key!)}
                style={{
                  padding: '8px 20px', borderRadius: R.md, border: `1px solid ${C.accent}`,
                  background: C.accentLt, color: C.accent, fontSize: T.sm, fontWeight: 700, cursor: 'pointer',
                }}>
                📥 재다운로드
              </button>
            )}
          </div>
        );
      }

      if (phase === 'reviewing') {
        return (
          <div style={{ padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: C.greenLt, borderRadius: R.md, marginBottom: 12 }}>
              <span style={{ fontSize: T.xl }}>✅</span>
              <div>
                <div style={{ fontSize: T.sm, fontWeight: 700, color: C.green }}>사업계획서 초안이 생성되었습니다</div>
                <div style={{ fontSize: T.xs, color: C.sub }}>Word 파일을 다운로드하여 검토하세요</div>
              </div>
            </div>
            {ext.docx_s3_key && (
              <button onClick={() => handleDownloadDocx(ext.docx_s3_key!)}
                style={{
                  padding: '10px 24px', borderRadius: R.md, border: 'none',
                  background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700,
                  cursor: 'pointer', marginBottom: 12,
                }}>
                📥 Word 파일 다운로드
              </button>
            )}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 4 }}>추가 지시사항</div>
              <textarea
                value={bizExtraInstructions}
                onChange={e => setBizExtraInstructions(e.target.value)}
                placeholder="수정이 필요한 부분을 입력하세요... (예: 3번 항목을 더 구체적으로)"
                rows={3}
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: R.sm,
                  border: `1px solid ${C.border}`, fontSize: T.xs, fontFamily: 'inherit',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>추가 참고 파일이 필요한 경우 위 설정에서 파일을 추가한 후 재생성하세요.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => handleRegenerateBizplan(s)} disabled={isStepLoading}
                style={{
                  padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.border}`,
                  background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600,
                  cursor: isStepLoading ? 'default' : 'pointer',
                }}>
                {isStepLoading ? '생성 중...' : '🔄 재생성'}
              </button>
              <button onClick={async () => {
                await updateExtras(s.id, { ...ext, phase: 'done' });
                onToast('완료 처리되었습니다', 'success');
              }} style={{
                padding: '8px 20px', borderRadius: R.md, border: 'none',
                background: C.green, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: 'pointer',
              }}>
                ✅ 완료
              </button>
            </div>
          </div>
        );
      }

      // phase === 'settings'
      return (
        <div style={{ padding: '10px 0' }}>
          <AIBizplanConfigPanel
            value={bizCfg}
            onChange={v => setBizConfigs(prev => ({ ...prev, [s.id]: v }))}
            presets={aiPresets}
          />
          <button
            onClick={() => handleGenerateBizplan(s)}
            disabled={isStepLoading}
            style={{
              marginTop: 12, padding: '10px 20px', borderRadius: R.md, border: 'none',
              background: isStepLoading ? C.muted : C.accent, color: C.white,
              fontSize: T.sm, fontWeight: 700, cursor: isStepLoading ? 'default' : 'pointer',
              width: '100%', textAlign: 'center',
            }}
          >
            {isStepLoading ? '🤖 AI가 사업계획서를 생성하고 있습니다...' : '▶ 사업계획서 생성하기'}
          </button>
          <div style={{ fontSize: T.xs, color: C.muted, textAlign: 'center', marginTop: 6 }}>
            보통 1~2분 소요됩니다
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div style={{ borderRadius: R.lg, border: `1px solid ${C.border}`, background: C.white, marginBottom: 8 }}>
      {/* 헤더 */}
      {!hideHeader && <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: T.lg }}>{editMode ? editTaskIcon : (task.icon || '📋')}</span>
        <div style={{ flex: 1 }}>
          {editMode ? (
            <input value={editTaskName} onChange={e => setEditTaskName(e.target.value)}
              style={{ fontSize: T.md, fontWeight: 700, border: `1px solid ${C.accent}`, borderRadius: R.sm, padding: '4px 8px', outline: 'none', width: '100%', boxSizing: 'border-box' as const }} />
          ) : (
            <div style={{ fontSize: T.md, fontWeight: 700 }}>{task.type}</div>
          )}
          <div style={{ fontSize: T.xs, color: C.muted }}>{company.name} · {doneCount}/{steps.length} 완료</div>
        </div>
        {editMode ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select value={editTaskStatus} onChange={e => setEditTaskStatus(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }}>
              <option value="진행">진행</option>
              <option value="대기">대기</option>
              <option value="완료">완료</option>
              <option value="FP요청대기">FP요청대기</option>
            </select>
            <input type="date" value={editTaskDeadline} onChange={e => setEditTaskDeadline(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
          </div>
        ) : (
          <>
            <div style={{ width: 60, height: 6, borderRadius: 3, background: C.bg, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: progress === 100 ? C.green : C.accent, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: T.xs, fontWeight: 700, color: progress === 100 ? C.green : C.accent }}>{progress}%</span>
            <select value={task.status} onClick={e => e.stopPropagation()} onChange={e => changeTaskStatus(e.target.value)}
              style={{ padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${STATUS_COLORS[task.status]?.color || C.border}`, background: STATUS_COLORS[task.status]?.bg || C.bg, color: STATUS_COLORS[task.status]?.color || C.text, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
              <option value="진행">진행</option>
              <option value="완료">완료</option>
              <option value="FP요청대기">FP요청대기</option>
              <option value="보류">보류</option>
              <option value="중단">중단</option>
            </select>
            <button onClick={enterEditMode} title="업무 수정"
              style={{ padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.sm }}>✏️</button>
          </>
        )}
      </div>}

      {/* 업무 중단 — 이유 하나 누르면 끝 (2026-10-12) */}
      {stopAsk && !editMode && (
        <div style={{ padding: '8px 14px', background: '#F9FAFB', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginRight: 4 }}>중단 이유</span>
          {STOP_REASONS.map(r => (
            <button key={r} onClick={() => stopTask(r)}
              style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>{r}</button>
          ))}
          <button onClick={() => setStopAsk(false)} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: R.sm, border: 'none', background: 'transparent', color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
        </div>
      )}
      {task.status === '중단' && !stopAsk && task.stop_reason && (
        <div style={{ padding: '6px 14px', fontSize: T.xs, color: isClosed(task) ? '#334155' : C.sub, fontWeight: isClosed(task) ? 700 : 400, borderBottom: `1px solid ${C.border}` }}>{isClosed(task) ? '' : '중단 · '}{isClosed(task) ? task.stop_reason.replace(CLOSE_PREFIX, `${CLOSE_PREFIX} —`).replace('— · ', '— ') : task.stop_reason}{task.stopped_at ? ' · ' + String(task.stopped_at).slice(0, 10) : ''}</div>
      )}

      {/* 편집 모드: 저장/취소 바 */}
      {editMode && (
        <div style={{ padding: '8px 14px', background: C.accentLt, borderBottom: `1px solid ${C.accent}33`, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: T.xs, color: C.accent, fontWeight: 600, flex: 1 }}>✏️ 편집 모드</span>
          <button onClick={cancelEdit}
            style={{ padding: '5px 14px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>취소</button>
          <button onClick={saveEdit} disabled={editSaving}
            style={{ padding: '5px 14px', borderRadius: R.sm, border: 'none', background: editSaving ? C.muted : C.accent, color: C.white, fontSize: T.xs, fontWeight: 700, cursor: editSaving ? 'default' : 'pointer' }}>
            {editSaving ? '저장 중...' : '💾 저장'}
          </button>
        </div>
      )}

      {/* 단계 목록 */}
      <div style={{ padding: '4px 10px 6px' }}>
        {/* ═══ 편집 모드 단계 목록 ═══ */}
        {editMode ? (
          <>
            {editSteps.map((es, si) => (
              <div key={es.id} style={{
                display: 'flex', gap: 6, alignItems: 'center', padding: '8px 10px',
                borderRadius: R.md, background: si % 2 === 0 ? C.bgAlt : 'transparent',
                marginBottom: 4, border: `1px solid ${C.border}`,
              }}>
                <span style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, width: 18, textAlign: 'center', flexShrink: 0 }}>{si + 1}</span>
                <select value={es.type} onChange={e => updateEditStep(si, 'type', e.target.value)}
                  style={{ padding: '5px 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', width: 90, flexShrink: 0 }}>
                  {STEP_TYPE_OPTS.map(t => <option key={t.key} value={t.key} disabled={t.key==='ai_questionnaire'||t.key==='ai_bizplan'}>{t.icon} {t.label}{t.key==='ai_questionnaire'||t.key==='ai_bizplan'?' (준비중)':''}</option>)}
                </select>
                <input value={es.name} onChange={e => updateEditStep(si, 'name', e.target.value)}
                  placeholder="단계명"
                  style={{ flex: 1, padding: '5px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', minWidth: 0 }} />
                {/* 결과 단계 말 — 고르면 끝낼 때 결과 두 단추(2026-10-28 · 하드코딩 대신 단계 설정) */}
                <JudgeSelect value={(es.extras as { judge?: Judge | null }).judge} width={96}
                  onChange={j => setEditSteps(prev => prev.map((s, i) => (i === si ? { ...s, extras: { ...s.extras, judge: j } as StepExtrasData } : s)))} />
                <input type="date" value={es.due} onChange={e => updateEditStep(si, 'due', e.target.value)}
                  style={{ padding: '5px 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', width: 120, flexShrink: 0 }} />
                <select value={es.status} onChange={e => updateEditStep(si, 'status', e.target.value)}
                  style={{ padding: '5px 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', width: 60, flexShrink: 0 }}>
                  <option value="대기">대기</option>
                  <option value="진행">진행</option>
                  <option value="완료">완료</option>
                </select>
                <button onClick={() => removeEditStep(si)} title="단계 삭제"
                  style={{ padding: '2px 6px', borderRadius: R.sm, border: 'none', background: C.redLt, color: C.red, fontSize: T.xs, cursor: 'pointer', flexShrink: 0 }}>✕</button>
              </div>
            ))}
            <button onClick={addEditStep}
              style={{ width: '100%', padding: '8px 0', borderRadius: R.md, border: `2px dashed ${C.border}`, background: 'transparent', color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', marginTop: 6 }}>
              + 단계 추가
            </button>
          </>
        ) : (
          /* ═══ 일반 모드 단계 목록 ═══ */
          <>
        {steps.map((s, si) => {
          const isExpanded = expanded === s.id;
          const stColor = roundBadge(task, s) || STATUS_COLORS[s.status] || STATUS_COLORS['대기'];   // 심사 결과 · 「다음 회차에」(2026-10-12)
          const ext = getExtras(s);
          const isCollect = s.type === 'collect';
          const isAI = s.type === 'ai_questionnaire' || s.type === 'ai_bizplan';
          const collectTotal = isCollect ? ((ext.collect_ids || []).length + (ext.custom_docs || []).length) : 0;
          const collectSubmitted = isCollect ? (ext.submitted_count ?? 0) : 0;
          const hasSent = isCollect && ext.collect_sent;
          const hasPhase = ext.phase;

          return (
            <div key={s.id} style={{ marginBottom: 0 }}>
              {/* 단계 행 */}
              <div onClick={() => setExpanded(isExpanded ? null : s.id)}
                onDragEnter={(e) => { if (stepDragIdx !== null && stepDragIdx !== si) { e.preventDefault(); setStepDragOver(si); } }}
                onDragOver={(e) => { if (stepDragIdx !== null) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                onDrop={(e) => { if (stepDragIdx !== null) { e.preventDefault(); onStepDrop(si); } }}
                style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px 7px 12px',
                cursor: 'pointer',
                background: stepDragOver === si ? '#EAF2FB' : isExpanded ? C.bgAlt : s.status === '진행' ? '#F8FAFF' : 'transparent',
                borderTop: stepDragOver === si ? '2px solid #2E75B6' : '2px solid transparent',
                borderBottom: `0.5px solid ${C.border}`,
                opacity: s.status === '대기' && !isExpanded ? 0.45 : 1,
              }}>
                {/* 순서 변경 + 타임라인 점 */}
                <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, gap: 0 }}>
                  {/* V21_DND:2-Ab — drag handle */}
                  <span draggable onDragStart={(e)=>{e.stopPropagation();setStepDragIdx(si);e.dataTransfer.effectAllowed='move';}} onDragEnd={(e)=>{e.stopPropagation();setStepDragIdx(null);setStepDragOver(null);}} onClick={(e)=>e.stopPropagation()} title="드래그하여 순서 변경" style={{cursor:'grab',fontSize:13,color:'#9CA3AF',padding:'2px 4px',lineHeight:1,userSelect:'none',opacity:stepDragIdx===si?0.4:0.75}}>⋮⋮</span>
                </div>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 600, flexShrink: 0,
                  ...(s.status === '완료' ? { background: '#0F6E56', color: '#fff' }
                    : s.status === '진행' ? { background: '#185FA5', color: '#fff' }
                    : { background: '#fff', color: C.muted, border: `1.5px solid ${C.border}` }),
                }}>{s.status === '완료' ? '✓' : si + 1}</div>

                <span style={{ fontSize: 12, flexShrink: 0 }}>{STEP_ICONS[s.type] || '🔧'}</span>
                {editingStepName===s.id ? (
                  <input type='text' defaultValue={s.name} autoFocus
                    onClick={e=>e.stopPropagation()}
                    onBlur={async(e)=>{const v=e.target.value.trim();setEditingStepName(null);if(v&&v!==s.name){try{await api.updateStep(task.id,s.id,{name:v} as Record<string,unknown>);onRefresh();onToast('단계명 수정됨','success');}catch{onToast('수정 실패','error');}}}}
                    onKeyDown={e=>{if(e.key==='Enter')(e.target as HTMLInputElement).blur();if(e.key==='Escape')setEditingStepName(null);}}
                    style={{flex:1,fontSize:12,fontWeight:600,padding:'1px 4px',borderRadius:3,border:'1px solid #2E75B6',outline:'none',minWidth:0}} />
                ) : (
                  <span onClick={e=>{e.stopPropagation();setEditingStepName(s.id);}} title='클릭하여 단계명 수정'
                    style={{ flex: 1, fontSize: 12, fontWeight: s.status === '진행' ? 600 : 400,
                      color: s.status === '완료' ? C.muted : C.text,
                      textDecoration: stepStruck(task, s) ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text',
                    }}>
                    {s.name}
                  </span>
                )}

                {/* 배지들 */}
                {isCollect && collectTotal > 0 && (
                  <Badge bg={hasSent ? C.greenLt : C.accentLt} color={hasSent ? C.green : C.accent} style={{ fontSize: 11 }}>
                    {hasSent ? `📤 ${collectSubmitted}/${collectTotal}` : `📎 ${collectTotal}건`}
                  </Badge>
                )}
                {isAI && hasPhase && hasPhase !== 'settings' && (
                  <Badge bg={hasPhase === 'done' ? C.greenLt : '#F5F3FF'} color={hasPhase === 'done' ? C.green : '#7C3AED'} style={{ fontSize: 11 }}>
                    {hasPhase === 'done' ? '✅완료' : hasPhase === 'reviewing' ? '🔍검토중' : '🤖생성중'}
                  </Badge>
                )}
                {editingStepDue===s.id ? (<input type='date' defaultValue={s.due?.slice(0,10)||''} autoFocus onClick={e=>e.stopPropagation()} onBlur={async(e)=>{const v=e.target.value;setEditingStepDue(null);if(v!==(s.due?.slice(0,10)||'')){/* V20_MASTER:3b */ await cascadeDueChange(s.id, s.due, v);}}} onKeyDown={e=>{if(e.key==='Enter')(e.target as HTMLInputElement).blur();if(e.key==='Escape')setEditingStepDue(null);}} style={{fontSize:11,padding:'2px 6px',borderRadius:4,border:'1px solid #2E75B6',outline:'none',width:100,flexShrink:0}}/>) : (<span onClick={e=>{e.stopPropagation();setEditingStepDue(s.id);}} title='클릭하여 기한 수정' style={{fontSize:11,color:s.due?C.muted:'#D1D5DB',cursor:'pointer',padding:'1px 4px',borderRadius:4,background:s.due?'transparent':'#F9FAFB',border:s.due?'none':'1px dashed #D1D5DB',flexShrink:0}}>{s.due?fmtDate(s.due):'기한'}</span>)}
                <Badge bg={stColor.bg} color={stColor.color} style={{ fontSize: 11 }}>{stColor.label}</Badge>
                <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginLeft: 2 }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* 확장 상세 */}
              {isExpanded && (
                <div style={{ padding: '0 14px 0 42px', background: C.bgAlt }}>
                  {/* 1줄 액션바: 유형 | 상태 | 삭제 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 0', borderBottom: (isCollect || isAI) ? `0.5px solid ${C.border}` : 'none' }}>
                    <select value={s.type || 'work'} onChange={async(e) => { e.stopPropagation(); try { await api.updateStep(task.id, s.id, { type: e.target.value } as Record<string, unknown>); onRefresh(); onToast('유형 변경됨', 'success'); } catch { onToast('변경 실패', 'error'); } }}
                      style={{ padding: '3px 6px', borderRadius: 4, border: `0.5px solid ${C.border}`, fontSize: 11, outline: 'none', background: C.white, color: C.sub, cursor: 'pointer' }}>
                      {STEP_TYPE_OPTS.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                    </select>
                    <span style={{ color: C.border, fontSize: 11, margin: '0 2px' }}>|</span>
                    {['대기', '진행', '회신대기', '완료'].map(st => (
                      <button key={st} onClick={(e) => { e.stopPropagation(); if (st === '회신대기') { const ex = getExtras(s); setWaitAsk({ stepId: s.id, who: ex.wait_for && ex.wait_for.length ? ex.wait_for : ['업체', 'FP'], q: ex.wait_question || '' }); return; } updateStepStatus(s.id, st); }}
                        style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: s.status === st ? 600 : 400, cursor: 'pointer',
                          border: `0.5px solid ${s.status === st ? STATUS_COLORS[st].color : C.border}`,
                          background: s.status === st ? STATUS_COLORS[st].bg : C.white,
                          color: s.status === st ? STATUS_COLORS[st].color : C.sub,
                        }}>{st}</button>
                    ))}
                    <label onClick={e => e.stopPropagation()} title="공고 마감처럼 정해진 날 — 앞 단계가 늦어져도 이 단계 기한은 밀리지 않아요"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6, fontSize: 11, color: getExtras(s).fixed_due ? '#92400E' : C.sub, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!getExtras(s).fixed_due} onChange={async e => { try { const r = await api.taskFlow(task.id, { action: 'fixed', stepId: s.id, fixed: e.target.checked }); if (!r.success) throw new Error(r.error || '실패'); onRefresh(); onToast(r.data ? r.data.message : '바꿨어요', 'success'); } catch (er: unknown) { onToast(er instanceof Error ? er.message : '실패', 'error'); } }} />📌 공고 마감
                    </label>
                    <button onClick={async(e) => { e.stopPropagation(); if (!window.confirm(`"${s.name}" 단계를 삭제하시겠습니까?`)) return; try { await api.deleteStep(task.id, s.id); setExpanded(null); onRefresh(); onToast('단계 삭제됨', 'success'); } catch { onToast('삭제 실패', 'error'); } }}
                      style={{ marginLeft: 'auto', padding: '2px 6px', borderRadius: 4, fontSize: 11, border: 'none', background: 'transparent', color: C.muted, cursor: 'pointer' }}>🗑</button>
                  </div>

                  {resultFor === s.id && <StepResultPick task={task} company={company} step={s} onRefresh={onRefresh} onToast={onToast} onClose={() => setResultFor(null)} />}
                  {/* 회신 대기 (2026-10-12) — 누구 답을 기다리나(기본 업체 · FP 둘 다, 누구든 먼저 답하면 끝) · 물어본 것은 고객 페이지 · FP 포털에 그대로 보인다 */}
                  {waitAsk && waitAsk.stepId === s.id && (
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderBottom: `0.5px solid ${C.border}` }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#0A7D74' }}>회신 대기</span>
                      {['업체', 'FP'].map(w => (
                        <label key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: C.text, cursor: 'pointer' }}>
                          <input type="checkbox" checked={waitAsk.who.includes(w)} onChange={() => setWaitAsk(p => (p ? { ...p, who: p.who.includes(w) ? p.who.filter(x => x !== w) : [...p.who, w] } : p))} />{w}
                        </label>
                      ))}
                      <input value={waitAsk.q} onChange={e => setWaitAsk(p => (p ? { ...p, q: e.target.value } : p))} placeholder="물어본 것 (선택 · 고객 · FP 화면에 보여요) 예: 견적대로 진행할지 알려 주세요"
                        style={{ flex: 1, minWidth: 220, padding: '4px 8px', borderRadius: 4, border: `0.5px solid ${C.border}`, fontSize: 11, outline: 'none' }} />
                      <button onClick={() => startWait(s, waitAsk.who, waitAsk.q)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#0A7D74', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>회신 대기로</button>
                      <button onClick={() => setWaitAsk(null)} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', background: 'transparent', color: C.muted, fontSize: 11, cursor: 'pointer' }}>취소</button>
                    </div>
                  )}
                  {s.status === '회신대기' && !(waitAsk && waitAsk.stepId === s.id) && (
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 0', borderBottom: `0.5px solid ${C.border}`, fontSize: 11, color: '#0A7D74' }}>
                      <span style={{ fontWeight: 700 }}>{(getExtras(s).wait_for || ['업체', 'FP']).join('·')} 회신 대기{getExtras(s).wait_since ? ' · ' + getExtras(s).wait_since + '부터' : ''}</span>
                      {getExtras(s).wait_question && <span style={{ color: C.sub }}>「{getExtras(s).wait_question}」</span>}
                      <span style={{ marginLeft: 'auto' }} />
                      {(getExtras(s).wait_for || ['업체', 'FP']).map(w => (
                        <button key={w} onClick={() => gotReply(s, w === '업체' ? '대표' : 'FP')}
                          style={{ padding: '3px 8px', borderRadius: 4, border: '0.5px solid #0A7D74', background: C.white, color: '#0A7D74', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{w} 회신 받음</button>
                      ))}
                    </div>
                  )}

                  {/* ═══ 탭 기반 UI (자료수집 / AI 질문지 / AI 사업계획서) ═══ */}
                  {(isCollect || isAI) && (() => {
                    const tabDefs: { label: string; icon: string }[] =
                      s.type === 'collect' ? [
                        { label: '리스트 작성·전송', icon: '📋' },
                        { label: '결과보기', icon: '📊' },
                      ] : s.type === 'ai_questionnaire' ? [
                        { label: '설정', icon: '⚙️' },
                        { label: '질문지 작성·전송', icon: '📋' },
                        { label: '결과보기', icon: '📊' },
                      ] : [
                        { label: '설정', icon: '⚙️' },
                        { label: '초안 생성', icon: '📄' },
                      ];
                    const activeTab = getTab(s.id);

                    return (
                      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                        {/* 탭 바 */}
                        <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: R.md, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                          {tabDefs.map((td, ti) => (
                            <button key={ti} onClick={() => setTab(s.id, ti)}
                              style={{
                                flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer',
                                background: activeTab === ti ? C.accent : C.white,
                                color: activeTab === ti ? '#fff' : C.sub,
                                fontSize: T.xs, fontWeight: activeTab === ti ? 700 : 500,
                                borderRight: ti < tabDefs.length - 1 ? `1px solid ${C.border}` : 'none',
                              }}>
                              {td.icon} {td.label}
                            </button>
                          ))}
                        </div>

                        {/* ═══ COLLECT 탭 내용 ═══ */}
                        {s.type === 'collect' && activeTab === 0 && (
                          <div style={{ paddingBottom: 16 }}>
                            <CollectStepConfigPanel
                              value={projectItemsOverride[s.id] || ext.collectConfig || { items: [], refFiles: [], refLinks: [], baseTemplateId: '' }}
                              onChange={v => {
                                const prevCfg = projectItemsOverride[s.id];
                                setProjectItemsOverride(prev => ({ ...prev, [s.id]: v }));
                                updateExtras(s.id, { ...ext, collectConfig: v });
                                if (ext.collect_project_id && prevCfg) syncLiveCollectItems(ext.collect_project_id, prevCfg.items, v.items);
                              }}
                              onToast={onToast}
                              projectId={ext.collect_project_id}
                              storageFiles={companyStorage}
                              onEnsurePortal={() => handleCollectStart(s, false)}
                              onProjectLinked={async (pid) => {
                                await updateExtras(s.id, { ...ext, collect_project_id: pid, collect_sent: true });
                                if (s.status !== '진행' && s.status !== '완료') {
                                  try { await api.updateStep(task.id, s.id, { status: '진행' } as Record<string, unknown>); } catch { /* noop */ }
                                }
                                setExpanded(null);
                                onRefresh();
                              }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 16 }}>
                              <button onMouseDown={() => {
                                // 포커스된 input/textarea가 있으면 blur를 강제해서 onChange가 먼저 실행되게
                                const ae = document.activeElement as HTMLElement | null;
                                if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) ae.blur();
                              }} onClick={() => {
                                // blur 후 state가 업데이트될 시간 (한 틱) 기다렸다가 저장
                                setTimeout(() => {
                                  saveCollectConfig(s.id, projectItemsOverride[s.id] || ext.collectConfig || { items: [], refFiles: [], refLinks: [], baseTemplateId: '' });
                                }, 50);
                              }}
                                style={{ padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
                                💾 저장
                              </button>
                              {!ext.collect_project_id && (
                                <button onClick={() => handleCollectStart(s)} disabled={loading === s.id || !ext.collectConfig?.items?.length}
                                  style={{
                                    flex: 1, padding: '8px 16px', borderRadius: R.md, border: 'none',
                                    background: (!ext.collectConfig?.items?.length || loading === s.id) ? C.bgAlt : C.accent,
                                    color: (!ext.collectConfig?.items?.length || loading === s.id) ? C.muted : '#fff',
                                    fontSize: T.sm, fontWeight: 700, cursor: (!ext.collectConfig?.items?.length || loading === s.id) ? 'default' : 'pointer',
                                  }}>
                                  {loading === s.id ? '생성 중...' : '▶ 자료수집 시작 (고객에게 전송)'}
                                </button>
                              )}
                              {ext.collect_project_id && (
                                <button onClick={() => setCollectNotifyProjectId(ext.collect_project_id!)}
                                  style={{ flex: 1, padding: '8px 16px', borderRadius: R.md, border: 'none', background: '#7C3AED', color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
                                  📤 알림 재발송
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {s.type === 'collect' && activeTab === 1 && (
                          <div>
                            {ext.collect_project_id ? (
                              <CollectResultView projectId={ext.collect_project_id} onToast={onToast} />
                            ) : (
                              <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: T.sm }}>
                                자료수집을 먼저 시작해주세요
                              </div>
                            )}
                          </div>
                        )}

                        {/* ═══ AI QUESTIONNAIRE 탭 내용 ═══ */}
                        {s.type === 'ai_questionnaire' && activeTab === 0 && (() => {
                          const aiCfg = aiConfigs[s.id] || ext.aiConfig || {
                            presetId: ext.ai_preset || '', files: [], extraPrompt: ext.ai_prompt || '',
                            phase: (ext.phase as AIStepConfig['phase']) || 'settings', result: ext.result || '',
                            includeCollectAnswers: ext.includeCollectAnswers,
                          };
                          const collectStep = steps.find(st => st.type === 'collect' && getExtras(st).collect_project_id);
                          const collectPid = collectStep ? getExtras(collectStep).collect_project_id : undefined;
                          return (
                            <div>
                              <AIStepConfigPanel
                                value={aiCfg}
                                onChange={v => setAiConfigs(prev => ({ ...prev, [s.id]: v }))}
                                presets={aiPresets}
                                aiType="ai_questionnaire"
                                collectProjectId={collectPid}
                                onViewCollect={collectPid ? () => setCollectDetailProjectId(collectPid) : undefined}
                              />
                              <button onClick={() => { handleGenerateQuestionnaire(s); setTab(s.id, 1); }} disabled={loading === s.id}
                                style={{
                                  marginTop: 16, padding: '12px 32px', borderRadius: R.md, border: 'none',
                                  background: loading === s.id ? C.muted : C.accent, color: C.white,
                                  fontSize: T.lg, fontWeight: 700, cursor: loading === s.id ? 'default' : 'pointer',
                                  width: '100%', textAlign: 'center',
                                }}>
                                {loading === s.id ? '🤖 AI가 질문지를 생성하고 있습니다...' : '▶ 질문지 생성하기'}
                              </button>
                            </div>
                          );
                        })()}
                        {s.type === 'ai_questionnaire' && activeTab === 1 && (() => {
                          const phase2 = ext.phase || 'settings';
                          const questions = editedQuestions || ext.result || '';
                          if (phase2 === 'settings' || !questions) {
                            return <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: T.sm }}>설정 탭에서 먼저 질문지를 생성해주세요</div>;
                          }
                          const parsedItems = questions.split('\n').filter((l: string) => l.trim()).map((l: string, i: number) => ({
                            num: i + 1, text: l.replace(/^\d+[\.)\]]\s*/, '').trim(),
                          }));
                          return (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>생성된 질문지 ({parsedItems.length}문항)</div>
                                <Badge bg={C.purpleLt || '#F5F3FF'} color={'#7C3AED'}>🤖 AI 생성</Badge>
                              </div>
                              <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden', marginBottom: 8 }}>
                                {parsedItems.map((item: {num:number;text:string}, idx: number) => (
                                  <div key={idx} style={{ padding: '8px 12px', borderBottom: idx < parsedItems.length - 1 ? `1px solid ${C.border}` : 'none', background: idx % 2 === 0 ? C.bgAlt : C.white, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: T.xs, fontWeight: 700, color: C.accent, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.num}</span>
                                    <span style={{ flex: 1, fontSize: T.sm, color: C.text }}>{item.text}</span>
                                    <span style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: R.sm, background: C.accentLt, color: C.accent, flexShrink: 0 }}>장문</span>
                                  </div>
                                ))}
                              </div>
                              <details style={{ marginBottom: 8 }}>
                                <summary style={{ fontSize: T.xs, color: C.muted, cursor: 'pointer', padding: '4px 0' }}>원본 텍스트 편집</summary>
                                <textarea value={questions} onChange={e => setEditedQuestions(e.target.value)} rows={10}
                                  style={{ width: '100%', padding: '10px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6, marginTop: 6 }} />
                              </details>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
                                <input value={modifyRequest} onChange={e => setModifyRequest(e.target.value)}
                                  placeholder="수정 요청 사항 입력..." onKeyDown={e => { if (e.key === 'Enter' && modifyRequest.trim()) handleRegenerateQuestionnaire(s, modifyRequest); }}
                                  style={{ flex: 1, padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                                <button onClick={() => handleRegenerateQuestionnaire(s, modifyRequest)} disabled={loading === s.id || !modifyRequest.trim()}
                                  style={{ padding: '7px 14px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: loading === s.id ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>수정</button>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => handleRegenerateQuestionnaire(s)} disabled={loading === s.id}
                                  style={{ padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: loading === s.id ? 'default' : 'pointer' }}>
                                  {loading === s.id ? '생성 중...' : '🔄 재생성'}
                                </button>
                                <button onClick={() => { handleConfirmQuestionnaire(s); setTab(s.id, 2); }} disabled={loading === s.id}
                                  style={{ padding: '8px 20px', borderRadius: R.md, border: 'none', background: loading === s.id ? C.muted : C.green, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: loading === s.id ? 'default' : 'pointer' }}>
                                  ✅ 컨펌 → 고객/FP 발송
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                        {s.type === 'ai_questionnaire' && activeTab === 2 && (
                          <div>
                            {ext.collect_project_id ? (
                              <CollectResultView projectId={ext.collect_project_id} onToast={onToast} />
                            ) : (
                              <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: T.sm }}>
                                질문지를 먼저 발송해주세요
                              </div>
                            )}
                          </div>
                        )}

                        {/* ═══ AI BIZPLAN 탭 내용 ═══ */}
                        {s.type === 'ai_bizplan' && activeTab === 0 && (() => {
                          const bizCfg = bizConfigs[s.id] || ext.bizplanConfig || {
                            presetId: ext.ai_preset || '', files: [], extraPrompt: ext.ai_prompt || '',
                            phase: (ext.phase as AIBizplanConfig['phase']) || 'settings',
                          };
                          return (
                            <div>
                              <AIBizplanConfigPanel value={bizCfg} onChange={v => setBizConfigs(prev => ({ ...prev, [s.id]: v }))} presets={aiPresets} />
                              <button onClick={() => { handleGenerateBizplan(s); setTab(s.id, 1); }} disabled={loading === s.id}
                                style={{
                                  marginTop: 16, padding: '12px 32px', borderRadius: R.md, border: 'none',
                                  background: loading === s.id ? C.muted : C.accent, color: C.white,
                                  fontSize: T.lg, fontWeight: 700, cursor: loading === s.id ? 'default' : 'pointer',
                                  width: '100%', textAlign: 'center',
                                }}>
                                {loading === s.id ? '🤖 AI가 사업계획서를 생성하고 있습니다...' : '▶ 사업계획서 생성하기'}
                              </button>
                              <div style={{ fontSize: T.xs, color: C.muted, textAlign: 'center', marginTop: 6 }}>보통 1~2분 소요됩니다</div>
                            </div>
                          );
                        })()}
                        {s.type === 'ai_bizplan' && activeTab === 1 && (() => {
                          const phase2 = ext.phase || 'settings';
                          if (phase2 === 'settings' && !ext.docx_s3_key) {
                            return <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: T.sm }}>설정 탭에서 먼저 사업계획서를 생성해주세요</div>;
                          }
                          return (
                            <div>
                              {ext.docx_s3_key && (
                                <div style={{ padding: '16px', background: C.greenLt, borderRadius: R.md, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <span style={{ fontSize: 28 }}>📄</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: T.md, fontWeight: 700, color: C.green }}>사업계획서 초안이 생성되었습니다</div>
                                    <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2 }}>Word 파일을 다운로드하여 검토하세요</div>
                                  </div>
                                  <button onClick={() => handleDownloadDocx(ext.docx_s3_key!)}
                                    style={{ padding: '10px 24px', borderRadius: R.md, border: 'none', background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
                                    📥 Word 다운로드
                                  </button>
                                </div>
                              )}
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 4 }}>추가 지시사항</div>
                                <textarea value={bizExtraInstructions} onChange={e => setBizExtraInstructions(e.target.value)}
                                  placeholder="수정이 필요한 부분을 입력하세요... (예: 3번 항목을 더 구체적으로)"
                                  rows={3} style={{ width: '100%', padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                              </div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                <button onClick={() => handleRegenerateBizplan(s)} disabled={loading === s.id}
                                  style={{ padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: loading === s.id ? 'default' : 'pointer' }}>
                                  {loading === s.id ? '생성 중...' : '🔄 재생성'}
                                </button>
                                <button onClick={async () => { await updateExtras(s.id, { ...ext, phase: 'done' }); onToast('완료 처리되었습니다', 'success'); }}
                                  style={{ padding: '8px 20px', borderRadius: R.md, border: 'none', background: C.green, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
                                  ✅ 완료
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}


                </div>
              )}
            </div>
          );
        })}
          </>
        )}

        {steps.length === 0 && !editMode && (
          <div style={{ textAlign: 'center', padding: 16, color: C.muted, fontSize: T.sm }}>등록된 단계가 없습니다</div>
        )}
        {!editMode && (
          <div style={{ padding: '8px 12px', borderTop: steps.length > 0 ? `0.5px solid ${C.border}` : 'none' }}>
            {addingStep ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <select value={newStepType} onChange={e => setNewStepType(e.target.value)}
                  style={{ padding: '4px 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }}>
                  {STEP_TYPE_OPTS.map(st => <option key={st.key} value={st.key}>{st.icon} {st.label}</option>)}
                </select>
                <input value={newStepName} onChange={e => setNewStepName(e.target.value)} placeholder="단계명"
                  autoFocus onKeyDown={e => { if (e.key === 'Enter' && newStepName.trim()) { api.addStep(task.id, { name: newStepName.trim(), type: newStepType, status: '대기' } as Partial<TaskStep>).then(() => { onRefresh(); onToast('단계 추가됨', 'success'); setNewStepName(''); setAddingStep(false); setNewStepType('work'); }).catch(() => onToast('추가 실패', 'error')); } if (e.key === 'Escape') { setAddingStep(false); setNewStepName(''); } }}
                  style={{ flex: 1, padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                <button onClick={async () => { if (!newStepName.trim()) return; try { await api.addStep(task.id, { name: newStepName.trim(), type: newStepType, status: '대기' } as Partial<TaskStep>); onRefresh(); onToast('단계 추가됨', 'success'); setNewStepName(''); setAddingStep(false); setNewStepType('work'); } catch { onToast('추가 실패', 'error'); } }}
                  style={{ padding: '4px 10px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>추가</button>
                <button onClick={() => { setAddingStep(false); setNewStepName(''); }}
                  style={{ padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, fontSize: T.xs, cursor: 'pointer', flexShrink: 0 }}>취소</button>
              </div>
            ) : (
              <button onClick={() => setAddingStep(true)}
                style={{ padding: '5px 14px', borderRadius: R.sm, border: `1px dashed ${C.accent}66`, background: `${C.accent}08`, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                + 단계 추가
              </button>
            )}
          </div>
        )}
      </div>

      {/* 자료수집 현황 모달 */}
      {collectDetailProjectId && (
        <CollectDetailModal
          projectId={collectDetailProjectId}
          onClose={() => setCollectDetailProjectId(null)}
          onToast={onToast}
        />
      )}

      {/* 자료수집 알림 발송 모달 */}
      {collectNotifyProjectId && (
        <CollectNotifyModal
          projectId={collectNotifyProjectId}
          companyName={company.name}
          ceo={ceoContact(company).name} ceoPhone={ceoContact(company).phone} ceoEmail={company.ceo_email}   /* 알림 받는 대표 = 실제 대표(있으면 · 2026-10-18) */
          manager={company.manager} managerPhone={company.manager_phone} managerEmail={company.manager_email}
          fpName={company.fps?.[0]?.name} fpPhone={company.fps?.[0]?.phone}
          onDone={() => { setCollectNotifyProjectId(null); onRefresh(); }}
          onCancel={() => setCollectNotifyProjectId(null)}
          onToast={onToast}
        />
      )}
    </div>
  );
}

/** 결과 단계 끝내기 — 승인 · 미승인(미승인이면 다시 신청 · 마감까지) · 업무 탭 지금 단계 막대와 같은 버튼(FlowActions) (2026-10-18) */
function StepResultPick({ task, company, step, onRefresh, onToast, onClose }: {
  task: Task; company: Company; step: TaskStep; onRefresh: () => void; onToast: (m: string, k?: 'success' | 'error') => void; onClose: () => void;
}) {
  const run = async (t: Task, body: FlowBody): Promise<boolean> => {
    try {
      const r = await api.taskFlow(t.id, body);
      if (!r.success) throw new Error(r.error || '바꾸지 못했어요');
      onClose(); onRefresh(); onToast(r.data ? r.data.message : '저장했어요', 'success');
      return true;
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '바꾸지 못했어요', 'error'); return false; }
  };
  const { bar, panel } = useFlowActions({ task, comp: company, step, kind: 'mine', run, compact: true, panelIndent: 0, resultOnly: true });
  return (
    <div onClick={e => e.stopPropagation()} style={{ padding: '8px 0', borderBottom: `0.5px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#B45309' }}>결과 단계예요 · 결과를 골라 주세요</span>
        {bar}
        <button onClick={onClose} style={{ marginLeft: 'auto', padding: '3px 8px', borderRadius: 4, border: 'none', background: 'transparent', color: C.muted, fontSize: 11, cursor: 'pointer' }}>취소</button>
      </div>
      {panel}
    </div>
  );
}
